import { useRef, useState } from 'react';
import { useActionLock } from '@breaker/action-lock';
import { useUpdateFleet, useUploadLogo } from '@/data/fleets';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/input';
import { ColorPicker } from '@/components/ui/color-picker';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/auth/api';
import type { Fleet } from '@breaker/shared-types';

/**
 * Brand basics — logo upload + the two brand colours. Extracted from
 * PortalBrandSettings for the signup wizard's "Your brand" step
 * (setup-wizard unit); Settings composes it back in unchanged. The portal
 * derives its full token set from the two hexes client-side.
 */
export function BrandEditor({
  fleet,
  canEdit,
  saveLabel = 'Save colours',
  onSaved,
}: {
  fleet: Fleet;
  canEdit: boolean;
  /** Wizard override — Settings keeps the default. */
  saveLabel?: string;
  /** Wizard hook: advance the step after a successful colours save. */
  onSaved?: () => void;
}) {
  const toast = useToast();
  const update = useUpdateFleet(fleet.id);
  const uploadLogo = useUploadLogo(fleet.id);
  const [primary, setPrimary] = useState(fleet.brandPrimaryHex ?? '#13293d');
  const [accent, setAccent] = useState(fleet.brandAccentHex ?? '#f2a03d');
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Unit D: the upload fires from the file input's onChange, not a Button
  // press, so the re-entrancy guard lives on a handler-level lock.
  const logoLock = useActionLock();

  function saveColours() {
    return update
      .mutateAsync({ brandPrimaryHex: primary, brandAccentHex: accent })
      .then(() => {
        toast.success('Brand colours saved.');
        onSaved?.();
      })
      .catch((err) =>
        toast.error(err instanceof ApiError ? err.message : 'Could not save colours.'),
      );
  }

  function onLogoPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      toast.error('Logo must be PNG, JPEG, or WebP.');
      return;
    }
    logoLock.run(() =>
      uploadLogo
        .mutateAsync(file)
        .then(() => toast.success('Logo uploaded.'))
        .catch((err) =>
          toast.error(err instanceof Error ? err.message : 'Upload failed.'),
        ),
    );
  }

  return (
    <div className="space-y-4">
      {/* Logo */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {fleet.logoUrl ? (
            <img
              src={fleet.logoUrl}
              alt="Fleet logo"
              className="h-10 max-w-[160px] object-contain rounded border border-breaker-line bg-white p-1"
            />
          ) : (
            <div className="h-10 w-24 rounded border border-dashed border-breaker-line-2 flex items-center justify-center text-2xs text-breaker-mute">
              No logo
            </div>
          )}
          <div>
            <p className="text-sm font-medium text-breaker-ink">Portal logo</p>
            <p className="text-xs text-breaker-mute">PNG, JPEG, or WebP — up to 2&nbsp;MB.</p>
          </div>
        </div>
        {canEdit ? (
          <>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={onLogoPick}
            />
            <Button
              size="sm"
              variant="secondary"
              loading={uploadLogo.isPending || logoLock.locked}
              onClick={() => logoInputRef.current?.click()}
            >
              Upload logo
            </Button>
          </>
        ) : null}
      </div>

      {/* Colours */}
      <div className="border-t border-breaker-line pt-4 flex flex-wrap items-end gap-4">
        <Field label="Primary colour" htmlFor="brandPrimary">
          <ColorPicker id="brandPrimary" value={primary} onChange={setPrimary} disabled={!canEdit} />
        </Field>
        <Field label="Accent colour" htmlFor="brandAccent">
          <ColorPicker id="brandAccent" value={accent} onChange={setAccent} disabled={!canEdit} />
        </Field>
        {canEdit ? (
          <Button size="sm" variant="secondary" action={saveColours}>
            {saveLabel}
          </Button>
        ) : null}
        <p className="basis-full text-xs text-breaker-mute">
          The portal derives hovers, washes, and readable text shades from these
          two automatically.
        </p>
      </div>
    </div>
  );
}
